// Cross-Border Operations — Bank Admin tab (Prompt 11).
// Five sections: Corridor Overview / Corridor Detail / Travel Rule / Sanctions / Local Rail Partners.

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Globe, Search, RefreshCw, X, Plus, Loader2, CheckCircle2, AlertTriangle,
  XCircle, Send, ShieldCheck, Wifi, FileText, ChevronRight,
  TrendingUp, TrendingDown, ExternalLink,
} from "lucide-react";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2/admin/cross-border`;

// ── Helpers ──

const fmtUSD = (n, dec = 2) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
const fmtUSDCompact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return fmtUSD(v);
};
const fmtRel = (iso) => {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

// Flag emoji for country codes (compact identifier)
const flag = (cc) => {
  if (!cc) return "";
  const codePoints = cc.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const STABLECOIN = "#a855f7";   // canonical stablecoin purple — dominant accent for cross-border
const EMERALD = "#10b981";

const STATUS_PILL = {
  active:     "bg-emerald-100 text-emerald-700",
  limited:    "bg-amber-100 text-amber-700",
  suspended:  "bg-rose-100 text-rose-700",
  connected:  "bg-emerald-100 text-emerald-700",
  degraded:   "bg-amber-100 text-amber-700",
  disconnected: "bg-rose-100 text-rose-700",
  sent:       "bg-emerald-100 text-emerald-700",
  pending:    "bg-amber-100 text-amber-700",
  failed:     "bg-rose-100 text-rose-700",
  retry_scheduled: "bg-sky-100 text-sky-700",
};

const StatusPill = ({ status }) => (
  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${STATUS_PILL[status] || "bg-slate-100 text-slate-700"}`}>
    {status?.replace(/_/g, " ") || "—"}
  </span>
);

const Toast = ({ msg, kind = "success", onClose }) => {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onClose, 2400);
    return () => clearTimeout(t);
  }, [msg, onClose]);
  if (!msg) return null;
  const bg = kind === "success" ? "bg-emerald-600" : kind === "info" ? "bg-sky-600" : "bg-rose-600";
  return (
    <div className={`fixed bottom-6 right-6 ${bg} text-white px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 z-50 text-sm`} data-testid="cb-toast">
      <CheckCircle2 className="w-4 h-4" />{msg}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, sublabel, tint = "purple" }) => {
  const tints = {
    purple: "bg-purple-50 text-purple-700",
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`cb-kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
        <span className={`p-1.5 rounded-lg ${tints[tint]}`}><Icon className="w-4 h-4" /></span>
      </div>
      <p className="text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
      {sublabel && <p className="text-xs text-slate-500 mt-1">{sublabel}</p>}
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 1 — Corridor Overview Dashboard
// ─────────────────────────────────────────────────

const OverviewSection = ({ data }) => {
  if (!data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0,1,2,3].map((i) => <div key={i} className="h-24 rounded-xl bg-slate-100 animate-pulse" />)}
        </div>
      </div>
    );
  }

  const maxVol = Math.max(...data.top_corridors.map((c) => c.volume_30d_usd), 1);

  return (
    <div className="space-y-5" data-testid="cb-overview-section">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Globe} label="Volume (30d)" value={fmtUSDCompact(data.kpis.total_volume_30d_usd)}
          sublabel={fmtUSD(data.kpis.total_volume_30d_usd, 0)} tint="purple" />
        <KpiCard icon={Send} label="Transactions (30d)" value={data.kpis.total_tx_30d.toLocaleString()}
          sublabel="Cross-border total" tint="sky" />
        <KpiCard icon={TrendingUp} label="Avg Tx Size" value={fmtUSD(data.kpis.avg_tx_usd, 0)}
          sublabel="Per remittance" tint="amber" />
        <KpiCard icon={TrendingUp} label="FX Margin (30d)" value={fmtUSDCompact(data.kpis.fx_margin_30d_usd)}
          sublabel="≈50 bps captured" tint="emerald" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="cb-top-corridors">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">Top 5 Active Corridors</h3>
          <span className="text-xs text-slate-500">By 30-day volume · stablecoin-routed</span>
        </div>
        <div className="space-y-3">
          {data.top_corridors.map((c) => {
            const pct = (c.volume_30d_usd / maxVol) * 100;
            return (
              <div key={c.id} className="grid grid-cols-12 gap-3 items-center" data-testid={`cb-top-corridor-${c.id}`}>
                <div className="col-span-3 flex items-center gap-2 min-w-0">
                  <span className="text-xl flex-shrink-0">{flag(c.country_code)}</span>
                  <span className="text-sm font-semibold text-slate-900 truncate">{c.label}</span>
                </div>
                <div className="col-span-6 relative h-8 bg-slate-100 rounded-md overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 rounded-md transition-all"
                    style={{ width: `${pct}%`, background: STABLECOIN, opacity: 0.85 }}
                  />
                  <div
                    className="absolute inset-y-0 right-0 w-1.5"
                    style={{ background: EMERALD, opacity: 0.9 }}
                    title="FX margin marker"
                  />
                </div>
                <div className="col-span-3 flex justify-end gap-4 text-xs">
                  <div className="text-right">
                    <p className="font-mono font-bold text-slate-900">{fmtUSDCompact(c.volume_30d_usd)}</p>
                    <p className="text-[10px] text-slate-500">{c.tx_count_30d.toLocaleString()} tx</p>
                  </div>
                  <div className="text-right border-l border-slate-200 pl-3">
                    <p className="font-mono font-bold text-emerald-600">{fmtUSDCompact(c.fx_margin_30d_usd)}</p>
                    <p className="text-[10px] text-slate-500">FX margin</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 2 — Corridor Detail Table + Modal
// ─────────────────────────────────────────────────

const CorridorDetailModal = ({ corridorId, onClose, toast }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!corridorId) return;
    setData(null);
    axios.get(`${API}/corridors/${corridorId}`).then((r) => setData(r.data));
  }, [corridorId]);

  if (!corridorId) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="cb-corridor-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {!data ? (
          <div className="h-64 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{flag(data.corridor.country_code)}</span>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">US → {data.corridor.country_name}</h3>
                  <p className="text-xs text-slate-500">{data.corridor.currency} · settles in ~{data.corridor.avg_settlement_minutes}m · {data.corridor.partner_names.length} partner(s)</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="cb-modal-close">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Volume chart */}
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-2">30-Day Volume</h4>
                <div className="h-48 bg-slate-50 rounded-lg p-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.daily_volume_series}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} />
                      <YAxis stroke="#94a3b8" fontSize={10} tickFormatter={fmtUSDCompact} />
                      <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                      <Area type="monotone" dataKey="volume_usd" stroke={STABLECOIN} fill={STABLECOIN} fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* FX rate trend */}
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-2">FX Rate Trend (USD → {data.corridor.currency})</h4>
                <div className="h-32 bg-slate-50 rounded-lg p-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.fx_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} hide />
                      <YAxis stroke="#94a3b8" fontSize={10} domain={["auto", "auto"]} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                      <Line type="monotone" dataKey="rate" stroke={EMERALD} dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Recent transactions */}
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-2">Recent Transactions</h4>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm" data-testid="cb-corridor-recent-tx">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                      <tr>
                        <th className="text-left px-3 py-2">ID</th>
                        <th className="text-left px-3 py-2">Sender</th>
                        <th className="text-left px-3 py-2">Recipient</th>
                        <th className="text-right px-3 py-2">USD</th>
                        <th className="text-right px-3 py-2">Local</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-left px-3 py-2">When</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.recent_transactions.map((t) => (
                        <tr key={t.id} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-mono text-xs text-slate-500">{t.id}</td>
                          <td className="px-3 py-2 text-slate-700">{t.sender_name}</td>
                          <td className="px-3 py-2 text-slate-700">{t.recipient_name}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtUSD(t.amount_usd)}</td>
                          <td className="px-3 py-2 text-right font-mono text-slate-500">{(t.amount_usd * t.fx_rate).toLocaleString("en-US", {maximumFractionDigits:0})} {t.settlement_currency}</td>
                          <td className="px-3 py-2"><StatusPill status={t.status === "completed" ? "sent" : "pending"} /></td>
                          <td className="px-3 py-2 text-xs text-slate-500">{fmtRel(t.timestamp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Partners */}
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-2">Partner Contacts</h4>
                {data.partners.length === 0 ? (
                  <p className="text-sm text-slate-400">No partners on this corridor</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {data.partners.map((p) => (
                      <div key={p.id} className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-bold text-slate-900">{p.partner_name}</p>
                          <StatusPill status={p.status} />
                        </div>
                        <p className="text-xs text-slate-500">{p.rail_type} · {p.settle_currency}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{p.contact_email} · {p.contact_phone}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const CorridorTableSection = ({ toast }) => {
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/corridors`).then((r) => setRows(r.data.corridors));
  }, []);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="cb-corridor-table-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">All Supported Corridors</h3>
          <p className="text-xs text-slate-500">{rows?.length || 0} corridors · click any row for full detail</p>
        </div>
      </div>

      {!rows ? (
        <div className="h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" data-testid="cb-corridor-table">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2.5">Corridor</th>
                <th className="text-right px-3 py-2.5">Volume (30d)</th>
                <th className="text-right px-3 py-2.5">Tx Count</th>
                <th className="text-right px-3 py-2.5">Avg Size</th>
                <th className="text-right px-3 py-2.5">FX Margin</th>
                <th className="text-right px-3 py-2.5">Settle</th>
                <th className="text-left px-3 py-2.5">Local Partner(s)</th>
                <th className="text-left px-3 py-2.5">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(c.id)} data-testid={`cb-corridor-row-${c.id}`}>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-2 font-medium text-slate-900">
                      <span className="text-lg">{flag(c.country_code)}</span>
                      US → {c.country_name}
                      <span className="text-[10px] text-slate-400 font-mono">{c.currency}</span>
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-slate-900">{fmtUSDCompact(c.volume_30d_usd)}</td>
                  <td className="px-3 py-3 text-right text-slate-700">{c.tx_count_30d.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right font-mono text-slate-600">{c.tx_count_30d ? fmtUSD(c.avg_tx_usd, 0) : "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-emerald-600">{fmtUSDCompact(c.fx_margin_30d_usd)}</td>
                  <td className="px-3 py-3 text-right text-xs text-slate-500">{c.avg_settlement_minutes ? `${c.avg_settlement_minutes}m` : "—"}</td>
                  <td className="px-3 py-3 text-xs text-slate-600 truncate max-w-[180px]" title={c.partner_names.join(" · ")}>{c.partner_names.join(" · ") || "—"}</td>
                  <td className="px-3 py-3"><StatusPill status={c.status} /></td>
                  <td className="px-3 py-3"><ChevronRight className="w-4 h-4 text-slate-300" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CorridorDetailModal corridorId={selected} onClose={() => setSelected(null)} toast={toast} />
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 3 — Travel Rule Compliance Pipeline
// ─────────────────────────────────────────────────

const TravelRuleDetailModal = ({ event, onClose, onResend, payload }) => {
  if (!event) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="cb-tr-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Travel Rule — IVMS 101 Payload</h3>
            <p className="text-xs text-slate-500 font-mono mt-0.5">{event.tx_id} · {fmtUSD(event.amount_usd)}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="cb-tr-modal-close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <StatusPill status={event.status} />
            <span className="text-xs text-slate-500">Last sent {fmtRel(event.data_sent_at)}</span>
            <span className="text-xs text-slate-500">→ {event.beneficiary_vasp}</span>
          </div>

          {payload && (
            <div>
              <h4 className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> IVMS 101 Data Payload (FATF R.16)
              </h4>
              <pre className="bg-slate-900 text-emerald-300 text-xs p-4 rounded-lg overflow-x-auto font-mono leading-relaxed" data-testid="cb-tr-payload">
{JSON.stringify(payload, null, 2)}
              </pre>
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-slate-200">
            <button onClick={() => onResend(event)} data-testid="cb-tr-resend-btn"
              className="px-4 py-2 text-xs font-bold rounded-md bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1.5">
              <Send className="w-3.5 h-3.5" /> Resend to VASP
            </button>
            <button onClick={onClose} className="px-4 py-2 text-xs font-semibold rounded-md text-slate-600 hover:bg-slate-100">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TravelRuleSection = ({ toast }) => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [payload, setPayload] = useState(null);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/travel-rule`);
    setData(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (e) => {
    setSelected(e);
    setPayload(null);
    const r = await axios.get(`${API}/travel-rule/${e.id}`);
    setPayload(r.data.ivms101_payload);
  };

  const resend = async (e) => {
    try {
      await axios.post(`${API}/travel-rule/${e.id}/resend`, { note: "Manual resend from admin console" });
      toast(`IVMS 101 resent to ${e.beneficiary_vasp}`);
      setSelected(null);
      await load();
    } catch (err) {
      toast(err.response?.data?.detail || "Resend failed", "error");
    }
  };

  if (!data) {
    return <div className="bg-white rounded-xl border border-slate-200 p-5 h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="cb-travel-rule-section">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-purple-600" /> Travel Rule Compliance
        </h3>
        <p className="text-xs text-slate-500">FATF R.16 — Originator/Beneficiary data exchange for transfers exceeding $3,000 USD</p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-slate-50 rounded-lg p-3" data-testid="cb-tr-kpi-total">
          <p className="text-[10px] uppercase font-semibold text-slate-600">Travel Rule txs (30d)</p>
          <p className="text-xl font-bold text-slate-900">{data.kpis.total_30d}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3" data-testid="cb-tr-kpi-sent">
          <p className="text-[10px] uppercase font-semibold text-emerald-700">Successfully sent</p>
          <p className="text-xl font-bold text-emerald-900">{data.kpis.sent} <span className="text-sm font-medium">({data.kpis.sent_pct}%)</span></p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3" data-testid="cb-tr-kpi-pending">
          <p className="text-[10px] uppercase font-semibold text-amber-700">Pending VASP response</p>
          <p className="text-xl font-bold text-amber-900">{data.kpis.pending}</p>
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="cb-tr-table">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2.5">Tx ID</th>
              <th className="text-right px-3 py-2.5">Amount</th>
              <th className="text-left px-3 py-2.5">Corridor</th>
              <th className="text-left px-3 py-2.5">Originator</th>
              <th className="text-left px-3 py-2.5">Beneficiary VASP</th>
              <th className="text-left px-3 py-2.5">Sent</th>
              <th className="text-left px-3 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.events.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(e)} data-testid={`cb-tr-row-${e.id}`}>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{e.tx_id}</td>
                <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-900">{fmtUSD(e.amount_usd)}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600 uppercase">{e.corridor_id}</td>
                <td className="px-3 py-2.5 text-slate-700">{e.originator_name}</td>
                <td className="px-3 py-2.5 text-slate-700">{e.beneficiary_vasp}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{fmtRel(e.data_sent_at)}</td>
                <td className="px-3 py-2.5"><StatusPill status={e.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TravelRuleDetailModal event={selected} onClose={() => setSelected(null)} onResend={resend} payload={payload} />
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 4 — Sanctions Screening Dashboard
// ─────────────────────────────────────────────────

const SanctionsSection = ({ toast }) => {
  const [status, setStatus] = useState(null);
  const [hits, setHits] = useState(null);
  const [search, setSearch] = useState({ name: "", country: "", dob: "" });
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sanctions/status`).then((r) => setStatus(r.data.lists));
    axios.get(`${API}/sanctions/hits`).then((r) => setHits(r.data.hits));
  }, []);

  const screen = async () => {
    if (!search.name.trim()) {
      toast("Name is required for screening", "error");
      return;
    }
    setSearching(true);
    setSearchResult(null);
    try {
      const r = await axios.post(`${API}/sanctions/screen`, search);
      setSearchResult(r.data);
    } catch (e) {
      toast(e.response?.data?.detail || "Screening failed", "error");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="cb-sanctions-section">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-purple-600" /> Sanctions Screening
        </h3>
        <p className="text-xs text-slate-500">OFAC + UN + EU + UK Sanctions Screening</p>
      </div>

      {/* List sync status cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {(status || [0,1,2,3]).map((s, i) => (
          <div key={s.id || i} className="bg-slate-50 rounded-lg p-3" data-testid={`cb-sanctions-list-${s.id || i}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <p className="text-xs font-bold text-slate-900">{s.label || "Loading…"}</p>
              </span>
              <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{s.status || "—"}</span>
            </div>
            <p className="text-xs text-slate-500">Last sync: <span className="font-medium text-slate-700">{s.last_synced_at ? fmtRel(s.last_synced_at) : "—"}</span></p>
            <p className="text-xs text-slate-500">{s.entry_count?.toLocaleString() || "—"} entries</p>
          </div>
        ))}
      </div>

      {/* Recent hits */}
      <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Recent Sanctions Hits</h4>
      {!hits ? (
        <div className="h-24 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : hits.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center">No sanctions hits in the last 30 days.</p>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden mb-5">
          <table className="w-full text-sm" data-testid="cb-sanctions-hits-table">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2.5">Timestamp</th>
                <th className="text-left px-3 py-2.5">Tx ID</th>
                <th className="text-left px-3 py-2.5">Member</th>
                <th className="text-left px-3 py-2.5">Match Type</th>
                <th className="text-left px-3 py-2.5">List</th>
                <th className="text-right px-3 py-2.5">Confidence</th>
                <th className="text-left px-3 py-2.5">Action Taken</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {hits.map((h) => (
                <tr key={h.id} className="hover:bg-slate-50" data-testid={`cb-sanctions-hit-${h.id}`}>
                  <td className="px-3 py-2.5 text-xs text-slate-500">{fmtRel(h.timestamp)}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{h.tx_id}</td>
                  <td className="px-3 py-2.5 text-slate-700">{h.member_name}</td>
                  <td className="px-3 py-2.5 text-slate-700">{h.match_type}</td>
                  <td className="px-3 py-2.5 text-xs uppercase font-mono text-slate-500">{h.list_source}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-700">{(h.confidence * 100).toFixed(0)}%</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${
                      h.action_taken.includes("Blocked") ? "bg-rose-100 text-rose-700" :
                      h.action_taken.includes("Flagged") ? "bg-amber-100 text-amber-700" :
                      h.action_taken.includes("FinCEN") ? "bg-purple-100 text-purple-700" :
                      "bg-emerald-100 text-emerald-700"
                    }`}>{h.action_taken}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Manual screening widget */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="cb-sanctions-manual">
        <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3">
          <Search className="w-4 h-4" /> Manual Sanctions Search
        </h4>
        <p className="text-xs text-slate-500 mb-3">Ad-hoc screening for incoming wire counterparties.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input type="text" placeholder="Full name (required)" value={search.name}
            onChange={(e) => setSearch({ ...search, name: e.target.value })}
            className="md:col-span-2 px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
            data-testid="cb-sanctions-search-name" />
          <input type="text" placeholder="Country (optional)" value={search.country}
            onChange={(e) => setSearch({ ...search, country: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
            data-testid="cb-sanctions-search-country" />
          <input type="text" placeholder="DOB YYYY-MM-DD (optional)" value={search.dob}
            onChange={(e) => setSearch({ ...search, dob: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
            data-testid="cb-sanctions-search-dob" />
        </div>
        <button onClick={screen} disabled={searching} data-testid="cb-sanctions-screen-btn"
          className="mt-3 px-4 py-2 text-xs font-bold rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1.5">
          {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          Screen Now
        </button>

        {searchResult && (
          <div className={`mt-3 rounded-lg p-3 border ${
            searchResult.verdict === "potential_match" ? "bg-rose-50 border-rose-200" :
            searchResult.verdict === "fuzzy_match" ? "bg-amber-50 border-amber-200" :
            "bg-emerald-50 border-emerald-200"
          }`} data-testid="cb-sanctions-search-result">
            <div className="flex items-start gap-2">
              {searchResult.verdict === "no_match" ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> :
               searchResult.verdict === "fuzzy_match" ? <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" /> :
               <XCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />}
              <div>
                <p className="text-sm font-bold">{searchResult.label}</p>
                <p className="text-xs mt-0.5">Confidence: {(searchResult.confidence * 100).toFixed(0)}% · {searchResult.list_source ? `List: ${searchResult.list_source.toUpperCase()}` : "All lists clear"}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 5 — Local Rail Partner Management
// ─────────────────────────────────────────────────

const PartnersSection = ({ toast }) => {
  const [rows, setRows] = useState(null);
  const [corridors, setCorridors] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    corridor_id: "", partner_name: "", rail_type: "Bank Direct",
    settle_currency: "", daily_cap: "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([
      axios.get(`${API}/partners`),
      axios.get(`${API}/corridors`),
    ]);
    setRows(p.data.partners);
    setCorridors(c.data.corridors);
  }, []);

  useEffect(() => { load(); }, [load]);

  const test = async (p) => {
    setTesting((t) => ({ ...t, [p.id]: true }));
    try {
      const r = await axios.patch(`${API}/partners/${p.id}/test`);
      if (r.data.ok) {
        toast(`${p.partner_name} OK — ${r.data.latency_ms}ms`, "success");
      } else {
        toast(`${p.partner_name} unreachable`, "error");
      }
    } catch {
      toast("Test failed", "error");
    } finally {
      setTesting((t) => ({ ...t, [p.id]: false }));
    }
  };

  const add = async () => {
    if (!form.corridor_id || !form.partner_name || !form.settle_currency || !form.daily_cap) {
      toast("All fields are required", "error");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/partners`, { ...form, daily_cap: Number(form.daily_cap) });
      toast(`Partner '${form.partner_name}' added`);
      setShowAdd(false);
      setForm({ corridor_id: "", partner_name: "", rail_type: "Bank Direct", settle_currency: "", daily_cap: "" });
      await load();
    } catch (e) {
      toast(e.response?.data?.detail || "Add failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!rows) return <div className="bg-white rounded-xl border border-slate-200 p-5 h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="cb-partners-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Local Rail Partners</h3>
          <p className="text-xs text-slate-500">{rows.length} destination-country partners across {new Set(rows.map((r) => r.country_code)).size} corridors</p>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} data-testid="cb-partners-add-toggle"
          className="px-3 py-1.5 text-xs font-semibold rounded-md bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Add Partner
        </button>
      </div>

      {showAdd && (
        <div className="bg-purple-50/50 border border-purple-200 rounded-lg p-4 mb-4" data-testid="cb-partners-add-form">
          <h4 className="text-sm font-bold text-slate-900 mb-3">Add New Partner</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Corridor</label>
              <select value={form.corridor_id}
                onChange={(e) => setForm({ ...form, corridor_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="cb-partners-form-corridor">
                <option value="">Select corridor…</option>
                {corridors.map((c) => (
                  <option key={c.id} value={c.id}>US → {c.country_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Partner Name</label>
              <input type="text" value={form.partner_name}
                onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
                placeholder="e.g., BBVA Mexico Direct"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                data-testid="cb-partners-form-name" />
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Rail Type</label>
              <select value={form.rail_type}
                onChange={(e) => setForm({ ...form, rail_type: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="cb-partners-form-rail">
                <option>Bank Direct</option>
                <option>Mobile Wallet</option>
                <option>Cash Pickup Network</option>
                <option>Local Stablecoin Off-ramp</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Settle Currency</label>
              <input type="text" value={form.settle_currency}
                onChange={(e) => setForm({ ...form, settle_currency: e.target.value.toUpperCase() })}
                placeholder="MXN" maxLength={3}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono uppercase"
                data-testid="cb-partners-form-ccy" />
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Daily Cap (USD)</label>
              <input type="number" value={form.daily_cap}
                onChange={(e) => setForm({ ...form, daily_cap: e.target.value })}
                placeholder="1000000"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono"
                data-testid="cb-partners-form-cap" />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={add} disabled={saving}
              className="px-4 py-2 text-xs font-bold rounded-md bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
              data-testid="cb-partners-form-submit">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add Partner"}
            </button>
            <button onClick={() => setShowAdd(false)}
              className="px-4 py-2 text-xs font-semibold rounded-md text-slate-600 hover:bg-slate-100">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="cb-partners-table">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2.5">Partner</th>
              <th className="text-left px-3 py-2.5">Country</th>
              <th className="text-left px-3 py-2.5">Rail Type</th>
              <th className="text-left px-3 py-2.5">Status</th>
              <th className="text-left px-3 py-2.5">Last Tested</th>
              <th className="text-right px-3 py-2.5">Daily Cap</th>
              <th className="text-left px-3 py-2.5">Settle</th>
              <th className="text-right px-3 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50" data-testid={`cb-partner-row-${p.id}`}>
                <td className="px-3 py-3 font-medium text-slate-900">{p.partner_name}</td>
                <td className="px-3 py-3 text-slate-700">
                  <span className="text-base mr-1.5">{flag(p.country_code)}</span>{p.country_name}
                </td>
                <td className="px-3 py-3 text-xs text-slate-600">{p.rail_type}</td>
                <td className="px-3 py-3"><StatusPill status={p.status} /></td>
                <td className="px-3 py-3 text-xs text-slate-500">{fmtRel(p.last_tested_at)}</td>
                <td className="px-3 py-3 text-right font-mono text-slate-700">{fmtUSDCompact(p.daily_cap_usd)}</td>
                <td className="px-3 py-3 text-xs font-mono uppercase text-slate-500">{p.settle_currency}</td>
                <td className="px-3 py-3 text-right">
                  <button onClick={() => test(p)} disabled={testing[p.id]}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-purple-600 hover:underline disabled:opacity-50"
                    data-testid={`cb-partner-test-${p.id}`}>
                    {testing[p.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wifi className="w-3 h-3" />}
                    Test
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────

export const CrossBorderOperationsTab = () => {
  const [overview, setOverview] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [toastState, setToastState] = useState({ msg: "", kind: "success" });
  const toast = (msg, kind = "success") => setToastState({ msg, kind });

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/overview`);
    setOverview(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRefresh = async () => {
    setRefreshing(true);
    try { await load(); } finally { setRefreshing(false); }
  };

  return (
    <div data-testid="admin-cross-border-operations">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Cross-Border Operations</h2>
          <p className="text-sm text-slate-500">
            International remittance corridors · FATF Travel Rule compliance · sanctions screening · local rail partners
          </p>
        </div>
        <button onClick={onRefresh} disabled={refreshing} data-testid="cb-refresh-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="space-y-6">
        <OverviewSection data={overview} />
        <CorridorTableSection toast={toast} />
        <TravelRuleSection toast={toast} />
        <SanctionsSection toast={toast} />
        <PartnersSection toast={toast} />
      </div>

      <Toast msg={toastState.msg} kind={toastState.kind} onClose={() => setToastState({ msg: "", kind: "success" })} />
    </div>
  );
};
